import { Injectable } from '@angular/core';
import { UnitsV2 } from '@schenck/rotec-components';
import { CorrectionMethod, ToleranceModes } from 'src/utils/enums/rotor-type.enum';
import { Adapter } from 'src/utils/interfaces/adapter-service.interfaces';
import { RotorTypeFormsData } from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import {
  CorrectionCalculation,
  CorrectionPlane,
  CorrectionWhenWithinTolerance,
} from 'src/utils/interfaces/rotor-type.interfaces';
import {
  mapDrillCorrectionPlane,
  mapMassCorrectionPlane,
  mapToCorrectionDistribution,
  mapWeightSetCorrectionPlane,
} from './adapter.mappers';

export class CorrectionCalculationModel implements CorrectionCalculation {
  constructor(
    public readonly correctionPlanes: CorrectionPlane[],
    public readonly calculateCorrectionWhenWithinTolerance?: CorrectionWhenWithinTolerance
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class CorrectionCalculationAdapter
  implements Adapter<RotorTypeFormsData, CorrectionCalculation>
{
  /**
   * Creates the adapted form data for correction calculation field
   *
   * @param formData: RotorTypeFormsData
   * @param units: UnitsV2[]
   * @returns CorrectionCalculation
   */
  adapt(
    { correctionMethodStep, correctionLocationsStep, tolerancesStep }: RotorTypeFormsData,
    units: UnitsV2[]
  ): CorrectionCalculation {
    const correctionPlanes = [correctionMethodStep.left, correctionMethodStep.right].filter(
      (item) => item && !!item.settings
    );

    return new CorrectionCalculationModel(
      correctionPlanes.map((item, index) => {
        return {
          ...((item.settings === CorrectionMethod.AddMass ||
            item.settings === CorrectionMethod.RemoveMass) &&
            mapMassCorrectionPlane(item, index)),
          ...(item.settings === CorrectionMethod.Drill &&
            mapDrillCorrectionPlane(item, index, units)),
          ...(item.settings === CorrectionMethod.WeightSets &&
            mapWeightSetCorrectionPlane(item, index, units)),
          correctionDistribution: mapToCorrectionDistribution(
            correctionLocationsStep[index === 0 ? 'left' : 'right']
          ),
        };
      }),

      {
        name: 'CalculationWithinTolerance',
        isEdited: true,
        value:
          tolerancesStep.toleranceMode.value === ToleranceModes.Manual
            ? (tolerancesStep.userDefined.selectedCalculation.value as boolean)
            : (tolerancesStep.isoCalculation.selectedCalculation.value as boolean),
      }
    );
  }
}
